import { useCallback, useEffect, useState } from 'react'
import { VAPID_PUBLIC_KEY } from '@shared/push/vapid-public-key'
import { base64UrlToBytes } from '@shared/push/base64url'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import type { DeviceFacts } from './push-state'

/**
 * Falha de push com mensagem para a tela.
 *
 * **A genérica "não foi possível ativar agora" escondia o motivo**, e "permissão
 * negada", "service worker travado" e "chave inválida" pedem coisas diferentes
 * de quem está olhando. A mensagem vai para a tela.
 */
export class PushError extends Error {}

/** Uma promessa com prazo. `serviceWorker.ready` não tem um. */
async function comPrazo<T>(promessa: Promise<T>, ms: number, oQue: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>
  const prazo = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new PushError(oQue))
    }, ms)
  })
  try {
    return await Promise.race([promessa, prazo])
  } finally {
    clearTimeout(timer!)
  }
}

/**
 * Os fatos deste aparelho, lidos das APIs do navegador.
 *
 * **Tudo defensivo de propósito.** Este código roda em Safari velho, em WebView
 * de app de mensagem, e no jsdom dos testes — os três com pedaços da API
 * faltando. Uma leitura direta de `Notification.permission` num contexto sem
 * `Notification` derruba a tela inteira por causa de uma seção que só oferece um
 * botão.
 */
const SEM_SUPORTE: DeviceFacts = {
  hasServiceWorker: false,
  hasPushManager: false,
  isIOS: false,
  isStandalone: false,
  permission: 'default',
  isSubscribed: false,
}

/**
 * Os fatos que dá para saber **na hora**, sem esperar nada.
 *
 * **Separado do `isSubscribed` de propósito, e isso foi um bug.** A primeira
 * versão lia tudo numa função só, com `await navigator.serviceWorker.ready` no
 * meio — e essa promessa é **ilimitada**: se o service worker não fica pronto,
 * ela nunca resolve, `facts` fica no padrão `SEM_SUPORTE`, e a tela mostra
 * "Este navegador não faz push" **sem botão nenhum**. É a pior mensagem
 * possível, porque é a única que diz "não há nada que você possa fazer" — e ela
 * apareceria num iPhone perfeitamente capaz.
 *
 * Agora o botão aparece assim que a tela monta, e `isSubscribed` chega depois.
 * O pior caso vira "oferecer ativar para quem já está ativo", que custa um
 * toque; antes era esconder o recurso de quem podia usá-lo.
 */
function fatosImediatos(): DeviceFacts {
  try {
    return lerFatos()
  } catch {
    // **Nada aqui pode derrubar a tela**, e isto não é zelo: ao mover a leitura
    // para a renderização, os testes reprovaram inteiros porque
    // `window.matchMedia` **não existe no jsdom** — e antes o erro era engolido
    // pelo `catch` do caminho assíncrono, então a função nunca tinha sido
    // segura de verdade. Um navegador sem `matchMedia` derrubaria a central de
    // notificações inteira por causa de uma seção que só oferece um botão.
    return SEM_SUPORTE
  }
}

function lerFatos(): DeviceFacts {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return SEM_SUPORTE

  const hasServiceWorker = 'serviceWorker' in navigator
  const hasPushManager = 'PushManager' in window
  // iPhone, iPad e iPod. O iPad moderno se anuncia como Mac, e o
  // `maxTouchPoints` é o que o separa de um Mac de verdade — é o teste que a
  // própria Apple recomenda desde o iPadOS 13.
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)
  const isStandalone =
    // `matchMedia` pode não existir — ver `fatosImediatos`. O `?.` aqui é o que
    // faz a checagem de suporte, logo abaixo, poder decidir sozinha.
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    // Safari no iOS não implementa `display-mode` em versões antigas; esta é a
    // propriedade não-padrão que ele expõe no lugar.
    (navigator as unknown as { standalone?: boolean }).standalone === true

  const permission =
    typeof Notification === 'undefined'
      ? ('default' as const)
      : (Notification.permission as 'default' | 'granted' | 'denied')

  return {
    hasServiceWorker,
    hasPushManager,
    isIOS,
    isStandalone,
    permission,
    // Otimista ao contrário: quem não sabe ainda diz "não inscrito", que é o
    // estado que **oferece** o botão. Ver o cabeçalho.
    isSubscribed: false,
  }
}

/**
 * Se este aparelho já tem inscrição. Espera o service worker — e é por isso que
 * mora fora de `fatosImediatos`.
 */
async function jaInscrito(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false
  try {
    const registro = await navigator.serviceWorker.ready
    return (await registro.pushManager.getSubscription()) !== null
  } catch {
    return false
  }
}

/**
 * Os fatos do aparelho e a ação de ativar o push.
 *
 * **A permissão é pedida DENTRO de `subscribe`**, e não na montagem: a nota da
 * integração manda pedir *"em contexto — depois que a pessoa entende o que vai
 * receber — nunca no primeiro segundo"*, porque *"permissão negada é
 * praticamente irreversível"*.
 */
export function usePush(): { facts: DeviceFacts; subscribe: () => Promise<void> } {
  const { notificationRepository } = useFinanceRepositories()
  // **Lido no inicializador, não num efeito.** São propriedades do navegador,
  // disponíveis na hora — e assim o primeiro quadro já mostra o botão certo em
  // vez de "sem suporte".
  const [facts, setFacts] = useState<DeviceFacts>(fatosImediatos)

  useEffect(() => {
    let live = true
    void jaInscrito().then((inscrito) => {
      if (live && inscrito) setFacts((atual) => ({ ...atual, isSubscribed: true }))
    })
    return () => {
      live = false
    }
  }, [])

  const subscribe = useCallback(async () => {
    if (typeof Notification === 'undefined') {
      throw new PushError('este navegador não tem a API de notificação')
    }
    const permissao = await Notification.requestPermission()
    if (permissao !== 'granted') {
      setFacts((atual) => ({ ...atual, permission: permissao }))
      throw new PushError(
        permissao === 'denied'
          ? 'a permissão foi negada — só dá para mudar nos ajustes do navegador'
          : 'a permissão não foi concedida',
      )
    }

    // **Com prazo.** `serviceWorker.ready` é ilimitado: sem o prazo, tocar em
    // "ativar" com o SW travado deixaria o botão em "Ativando…" para sempre,
    // sem erro e sem sucesso — o pior dos dois.
    const registro = await comPrazo(
      navigator.serviceWorker.ready,
      10_000,
      'o service worker não ficou pronto',
    )
    const inscricao = await registro.pushManager.subscribe({
      // **Obrigatório, e obrigatório `true`.** Uma inscrição sem payload visível
      // é recusada pelos navegadores modernos — e o payload é exatamente o que
      // faz a notificação dizer alguma coisa.
      userVisibleOnly: true,
      // Bytes, não a string. Ver `base64UrlToBytes`.
      applicationServerKey: base64UrlToBytes(VAPID_PUBLIC_KEY),
    })

    const bruto = inscricao.toJSON()
    const p256dh = bruto.keys?.['p256dh']
    const auth = bruto.keys?.['auth']
    if (bruto.endpoint === undefined || p256dh === undefined || auth === undefined) {
      throw new PushError('a inscrição veio sem endpoint ou sem chaves')
    }

    await notificationRepository.savePushSubscription({
      endpoint: bruto.endpoint,
      keys: { p256dh, auth },
    })
    setFacts((atual) => ({ ...atual, permission: 'granted', isSubscribed: true }))
  }, [notificationRepository])

  return { facts, subscribe }
}
